import { Button } from "../ui/button";
import { ArrowRight, Plus, Trash2, UserCheck, UserPlus } from "lucide-react";
import { Card, CardContent } from "../ui/card";
// @ts-ignore
import { Checkbox } from "@/components/ui/checkbox";
import OTPVerification from "./OTPVeification";
import { useWizard, Wizard } from "react-use-wizard";
import { useMarathonCartStore } from "@/lib/checkoutDetails/checkoutDetails";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import MembersRegistrationForm from "./MembersRegistrationForm";
import MarathonOtpVerification from "./MarathonOtpVerification";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// @ts-ignore
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { deleteMember } from "@/api/apiFunction";
import UpdatedMemberRegistration from "./UpdatedMemberRegistration";
import { calculateAge } from "@/lib/utils";
import { useToast } from "@/components/hooks/use-toast";
import { Image_BASE_URL } from "@/api/apiUrl";

interface Member {
  id: string;
  name: string;
  email: string;
  mobile: string;
  phoneCode: string;
  birthDate: string;
  gender: string;
  jerseySize: string;
  bloodGroup: string;
  profileImage: string;
  user: {
    status: "REJECTED" | "VERIFIED";
    profileImage: string;
  };
}

interface MembersListProps {
  allMembersData: Member[];
}

const MembersList = ({ allMembersData }: MembersListProps) => {
  const { toast } = useToast();
  const [mobileNumber, setMobileNumber] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [_, setSelectedMember] = useState<Member | null>(null);
  const queryClient = useQueryClient();
  const { previousStep } = useWizard();
  const { selectedUsers, addSelectedUser, removeSelectedUser } =
    useMarathonCartStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // deleting member
  const { mutate: deleteMutate } = useMutation({
    mutationFn: deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-members"] });

      toast({
        title: "Member deleted successfully!",
        variant: "default",
      });
      // Optionally, refetch or update the members list here
      console.log("deleted successfully");
    },
    onError: (error: any) => {
      console.error("Error deleting member:", error);
      toast({
        title: "Delete Failed!",
        description: `${error?.response?.data?.message || "An error occurred"}`,
        variant: "destructive",
      });
    },
  });

  interface HandleDeleteParams {
    memberId: string;
  }

  const handleDelete = ({ memberId }: HandleDeleteParams) => {
    deleteMutate(memberId);
    removeSelectedUser(memberId);
  };

  const handleCardClick = (member: Member) => {
    if (member?.user?.status == "REJECTED") return;
    const isMemberSelected = selectedUsers.find(
      (user) => user.id === member.id
    );

    if (isMemberSelected) {
      removeSelectedUser(member.id);
    } else {
      //@ts-ignore
      addSelectedUser(member);
    }
  };

  return (
    <div className="flex mx-3 flex-col items-center mb-24 md:mb-36 max-w-4xl md:mx-auto border bg-white rounded-lg shadow-lg">
      <h1 className="my-4 md:my-6 text-center font-semibold text-2xl md:text-3xl">
        Members
      </h1>

      <div className="w-full px-3 md:px-10">
        <div className="flex justify-end ">
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (open) {
                setUserInfo(null);
              }
            }}
          >
            <DialogTrigger>
              <div className="flex justify-center items-center text-xs bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2 md:py-2.5 px-4 md:px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md gap-1 w-28 md:w-32">
                <Plus className="w-3 md:w-4" /> ADD
              </div>
            </DialogTrigger>
            <DialogContent className="w-[95vw] md:w-full max-h-[90vh] overflow-y-auto">
              <div>
                <Wizard>
                  <ChooseMemberType />
                  <OTPVerification
                    setMobileNumber={setMobileNumber}
                    mobileNo={allMembersData[0].mobile}
                  />
                  <MarathonOtpVerification
                    mobileNumber={mobileNumber}
                    setUserInfo={setUserInfo}
                    userInfo={userInfo}
                  />
                  <MembersRegistrationForm
                    //@ts-ignore
                    userInfo={userInfo}
                    dialougeClose={handleDialogClose}
                    action="edit"
                  />
                  <MembersRegistrationForm
                    //@ts-ignore
                    userInfo={userInfo}
                    dialougeClose={handleDialogClose}
                    action="add"
                  />
                </Wizard>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {allMembersData?.map((member) => {
          const isSelected = selectedUsers.some(
            (user) => user.id === member.id
          ); // check if member is selected
          return (
            <Card
              key={member.id}
              className={`w-full font-sans border mt-4 py-2 md:py-3 rounded-lg transition-all duration-200 ease-in-out hover:shadow-md ${
                isSelected
                  ? "border-[#009FA6] border-2 bg-[#F8FDFD]"
                  : "border-gray-200"
              } ${
                member?.user?.status === "REJECTED"
                  ? "opacity-60 cursor-not-allowed bg-gray-50"
                  : "hover:border-[#009FA6]/50"
              }`}
              onClick={() => handleCardClick(member)}
            >
              <CardContent className="w-full relative p-3 md:p-6">
                <div className="w-full flex text-xs justify-between mt-1 md:mt-3 gap-2 md:gap-5">
                  <div className="max-w-[85%] md:max-w-[90%] w-full">
                    <div className="flex items-start md:items-center justify-start gap-2 md:gap-3">
                      <Avatar className="h-8 w-8 md:h-12 md:w-12 ring-2 ring-gray-100 flex-shrink-0">
                        <AvatarImage
                          src={`${Image_BASE_URL}/images/profile/${member?.user?.profileImage}`}
                        />
                        <AvatarFallback>
                          <div className="bg-[#E6F6F7] w-full h-full rounded-full text-[#009FA6] font-semibold flex items-center justify-center text-xs md:text-base">
                            {member.name[0].toLocaleUpperCase()}
                          </div>
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-sm space-y-0.5 md:space-y-1">
                        <p className="font-medium text-gray-900 text-xs md:text-sm truncate max-w-[150px] md:max-w-full">
                          {member.name}
                        </p>
                        <p className="text-gray-500 text-xs truncate max-w-[180px] md:max-w-full">
                          {member.email}
                        </p>
                        <p className="text-[#009FA6] bg-[#E6F6F7] px-2 py-0.5 rounded-full inline-block text-xs whitespace-nowrap">
                          Age: {calculateAge(`${member.birthDate}`)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-0 md:top-3 right-2 flex flex-col justify-center items-center gap-1 md:gap-2">
                    <div className="p-1">
                      <Checkbox
                        className="border-2 size-4 md:size-5 rounded-sm data-[state=checked]:bg-[#009FA6] data-[state=checked]:border-[#009FA6]"
                        checked={isSelected}
                        disabled={member?.user?.status == "REJECTED"}
                      />
                    </div>

                    <div className="p-1 hover:bg-[#E6F6F7] rounded-full transition-colors">
                      <UpdatedMemberRegistration
                        // @ts-ignore
                        updatedInfo={member}
                      />
                    </div>

                    <div className="cursor-pointer p-1 hover:bg-red-50 rounded-full transition-colors">
                      <AlertDialog>
                        <AlertDialogTrigger
                          onClick={() => setSelectedMember(member)}
                          className="text-gray-500 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white max-w-[95vw] md:max-w-lg">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-lg md:text-xl font-semibold">
                              Are you absolutely sure?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-600 text-sm">
                              This action cannot be undone. This will
                              permanently delete user and remove your any data
                              of user.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-gray-200 hover:bg-gray-100 transition-colors text-sm">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleDelete({ memberId: member.id })
                              }
                              className="bg-red-500 hover:bg-red-600 transition-colors text-sm"
                            >
                              Continue
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
                {member?.user?.status == "REJECTED" && (
                  <div className="w-full flex items-center mt-3">
                    <p className="text-center text-sm text-red-500 bg-red-50 px-3 py-2 rounded-md w-full">
                      Aadhar Verification is rejected
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="md:py-3">
        <Button
          onClick={previousStep}
          className="bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2.5 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md my-6"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default MembersList;

function ChooseMemberType() {
  const { nextStep, goToStep } = useWizard();
  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Choose Your Member Type
      </h1>
      <div className="space-y-4">
        <button
          onClick={nextStep}
          className="w-full text-left transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          <div className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
            <UserCheck className="w-8 h-8 text-blue-600 mr-4" />
            <div>
              <h2 className="text-lg font-semibold text-blue-800">
                Existing Member
              </h2>
              <p className="text-sm text-blue-600">
                Add an existing member to continue.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-500 ml-auto" />
          </div>
        </button>

        <button
          onClick={() => goToStep(4)}
          className="w-full text-left transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
        >
          <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100">
            <UserPlus className="w-8 h-8 text-green-600 mr-4" />
            <div>
              <h2 className="text-lg font-semibold text-green-800">
                New Member
              </h2>
              <p className="text-sm text-green-600">
                Register a new member to get started.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-green-500 ml-auto" />
          </div>
        </button>
      </div>
    </div>
  );
}
